package com.writerslab

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

class MainViewModel(
    /** Analytics facade; production code in `main()` passes the live
     *  [DefaultAnalyticsEvents], unit tests substitute a fake that
     *  records the calls so they can assert on them. */
    private val analytics: AnalyticsEvents = DefaultAnalyticsEvents(),
) : BaseViewModel() {
    // Auth
    var isLoggedIn by mutableStateOf(Auth.isLoggedIn)
        private set
    var userName by mutableStateOf<String?>(null)
        private set
    // Credit-pricing snapshot from GET /api/pricing. Hydrated once on
    // bootstrap and provided to the UI tree via LocalPricing — every
    // CTA's price tag and the teaser breakdown read from this object,
    // never from inline literals. Defaults match the backend so the
    // brief window before the fetch lands still renders sane numbers.
    var pricing by mutableStateOf(Pricing())
        private set
    var credits by mutableIntStateOf(0)
        private set
    var freeShortRemaining by mutableIntStateOf(0)
        private set
    var freeShortCharThreshold by mutableIntStateOf(30000)
        private set
    /** Lifetime free pool remaining for annotation generations. */
    var freeAnnotationsRemaining by mutableIntStateOf(0)
        private set
    /** Lifetime free pool remaining for full cover-batches (3 covers each). */
    var freeCoverBatchesRemaining by mutableIntStateOf(0)
        private set
    /** Lifetime free pool remaining for teaser scene previews. */
    var freeTeaserPreviewsRemaining by mutableIntStateOf(0)
        private set
    var emailVerified by mutableStateOf(false)
        private set
    var verificationCooldownSeconds by mutableIntStateOf(0)
        private set
    private var cooldownJob: Job? = null
    // Static analysis
    var pickedFileName by mutableStateOf<String?>(null)
        private set
    var bookId by mutableStateOf<String?>(null)
        private set
    var analysisState by mutableStateOf(AnalysisState.Idle)
        private set
    var analysisResult by mutableStateOf<AnalysisResult?>(null)
        private set
    var errorMessage by mutableStateOf<String?>(null)
        private set

    /**
     * Raw JSON of the current book's static analysis, mirrored here so
     * [persistCache] can write it to the local snapshot without going
     * back to [BookRepository.lastAnalysisJson] (which only reflects
     * fetches that came from the network — `restoreAnalysisState` and
     * the cache-hydrate path also populate this).
     *
     * Plain `var`, not Compose state — nothing in the UI subscribes to
     * the raw JSON, only to the parsed `analysisResult`.
     */
    private var currentAnalysisJson: String? = null

    // AI pipeline
    var aiState by mutableStateOf(AnalysisState.Idle)
        private set
    var aiReport by mutableStateOf<AiReport?>(null)
        private set
    var aiStageName by mutableStateOf("")
        private set
    var aiStage by mutableIntStateOf(0)
        private set
    var aiTotalStages by mutableIntStateOf(7)
        private set
    var aiChunk by mutableIntStateOf(0)
        private set
    var aiTotalChunks by mutableIntStateOf(0)
        private set
    var aiEtaSeconds by mutableStateOf<Int?>(null)
        private set
    var aiElapsedSeconds by mutableIntStateOf(0)
        private set

    // Promo annotation (one of the promo-materials cards). State machine
    // is local to the card UI so timers don't leak across books, but
    // result + error come from the server. `annotationLoading` is the
    // "request in flight" flag; the card uses it to drive its spinner.
    var annotationResult by mutableStateOf<AnnotationResult?>(null)
        private set
    var annotationLoading by mutableStateOf(false)
        private set
    var annotationError by mutableStateOf<String?>(null)
        private set

    // Promo covers. 3 slots; each variant has its own result + loading
    // state so per-variant regen shows a spinner only on that slot.
    // Map/Set are replaced wholesale on each mutation so Compose
    // picks up the change.
    var coverResults by mutableStateOf<Map<Int, CoverResult>>(emptyMap())
        private set
    var coverLoading by mutableStateOf<Set<Int>>(emptySet())
        private set
    var coverError by mutableStateOf<String?>(null)
        private set

    // Promo teaser. Two-step UX:
    //   1. generateTeaserPreview() → keyframe + scene (~30s, ~$0.04)
    //   2a. generateTeaserVideo() → adds i2v video to the same keyframe (~2 min, ~$0.56)
    //   2b. regenerateTeaserScene() → new keyframe + scene (replaces preview)
    // teaserLoadingKind tells the UI what's currently running so it can
    // show the right copy ("Generating keyframe..." vs "Generating video...").
    var teaserResult by mutableStateOf<TeaserResult?>(null)
        private set
    var teaserLoadingKind by mutableStateOf<TeaserLoadingKind?>(null)
        private set
    var teaserError by mutableStateOf<String?>(null)
        private set

    val teaserLoading: Boolean get() = teaserLoadingKind != null

    /**
     * Per-current-book bookkeeping for the backend's promo-job
     * registry. Two roles:
     *  - Cancel the local polling coroutine when the user switches
     *    books so its `try/finally` doesn't write the previous book's
     *    result into the new book's VM state. The BACKEND job keeps
     *    running — that's the whole point of the registry; the user
     *    will see the loader again when they navigate back via
     *    [reattachPromoJobs] in [selectFromHistory].
     *  - Defend against double-starts: a fresh user click is no-op
     *    if a coroutine is already in flight for the same key.
     *
     * The key is `"ANNOTATION" | "COVER_BATCH" | "COVER_REGEN:N" |
     * "TEASER"` so cover slots can be tracked independently and the
     * teaser collapses across all three sub-modes (preview/video/
     * regenerate) — only one teaser job per book runs at once.
     */
    private val promoCoroutineJobs = mutableMapOf<String, kotlinx.coroutines.Job>()

    // Tracks the in-flight AI pipeline coroutine (either a fresh
    // `runAiAnalysis` run or a `resumeRunningPipeline` re-attach). On book
    // switch [clearPerBookData] cancels it so its polling loop and any
    // post-await writes can't land on the next book's VM state.
    private var aiPipelineJob: Job? = null

    // Tracks the in-flight [selectFromHistory] coroutine so a fast
    // subsequent book click cancels the pending one before it lands a
    // stale `analysisResult` / `analysisState` write on the new book's
    // screen. Companion to [aiPipelineJob] — same cross-book write
    // class of bug, different coroutine. Cancelled in [selectFromHistory]
    // itself (not [clearPerBookData], which is called from inside the
    // coroutine and would self-cancel).
    private var bookSwitchJob: Job? = null

    private fun promoKey(kind: String, variant: Int? = null): String =
        if (variant != null) "$kind:$variant" else kind

    // UI
    var snackbarMessage by mutableStateOf<String?>(null)
        private set
    private var nudgeJob: Job? = null

    fun showNudge(message: String) {
        snackbarMessage = message
        nudgeJob?.cancel()
        nudgeJob = viewModelScope.launch {
            delay(4000)
            snackbarMessage = null
        }
    }

    var showTopUpDialog by mutableStateOf(false)
        private set

    // ── Editing the text in the reader ───────────────────────────────────

    /** Bought credits at least once (or switched on in the office) — may
     *  edit book texts in the reader. */
    var canEditText by mutableStateOf(false)
        private set

    fun onTextEditLocked(source: String) = analytics.textEditLocked(source)

    /** «Выбрать пакет» on the reader's card: sign in first, else top up —
     *  the first purchase opens text editing. */
    fun onChoosePackForEditing() {
        if (!isLoggedIn) { signIn(); return }
        openTopUpDialog()
    }

    fun onTextEditStarted() = analytics.textEditStarted()

    /** The reader saved the text: take the new static analysis, note that
     *  the deep-analysis report may now be about the old text, refresh the
     *  history row (word count). */
    fun onTextSaved(res: TextEditOutcome.Saved, paragraphsChanged: Int) {
        res.analysis?.let { analysisResult = it }
        res.analysisJson?.let { currentAnalysisJson = it }
        aiReport = aiReport?.copy(textChanged = res.reportTextChanged, textEditedAt = res.editedAt)
        analytics.textSaved(paragraphsChanged, res.marksDropped)
        persistCache()
        viewModelScope.launch { refreshHistory() }
    }

    fun onTextSaveFailed(reason: String) = analytics.textSaveFailed(reason)

    fun onTextDownloaded() = analytics.textDownloaded()

    /** A re-check of edits finished: credits or a free slot may be spent. */
    fun onTextRechecked(cost: Int, removed: Int, added: Int) {
        analytics.textRechecked(cost, removed, added)
        viewModelScope.launch { refreshUserCounters() }
    }

    fun onTextRecheckFailed(reason: String) {
        analytics.textRecheckFailed(reason)
        viewModelScope.launch { refreshUserCounters() }
    }

    fun openTopUpDialog() {
        if (!showTopUpDialog) analytics.topupDialogOpened()
        showTopUpDialog = true
    }

    // Real-money checkout state. `checkoutInFlight` guards double-clicks
    // on a pack card and stays up while the tab leaves for the payment
    // page. `paymentToast` is set once the watched payment settles; Main
    // observes it via LaunchedEffect to show the toast and then clears it
    // back to null.
    var checkoutInFlight by mutableStateOf(false)
        private set
    /** `success(n)` / `canceled` / `error` / null. Drives the
     *  post-checkout toast in Main without coupling the VM to the
     *  toast subsystem. */
    sealed class PaymentToast {
        data class Success(val creditsAdded: Int) : PaymentToast()
        object Canceled : PaymentToast()
        object Error : PaymentToast()
    }
    var paymentToast by mutableStateOf<PaymentToast?>(null)

    fun clearPaymentToast() { paymentToast = null }

    /** The checkout the user left for and we're still waiting on, or
     *  null. Drives the «waiting for payment» banner. */
    var pendingPayment by mutableStateOf<PendingPayment?>(null)
        private set
    /** Shown once a check finds [pendingPayment] still open — so a payment
     *  that has already gone through doesn't flash «waiting» first. */
    var pendingPaymentBannerVisible by mutableStateOf(false)
        private set
    /** No «canceled» toast for this one: the user closed the banner, or
     *  it's a record from an earlier visit. Success is always announced. */
    private var pendingPaymentQuiet = false
    private var pendingPaymentWatch: Job? = null
    private var pendingPaymentCheck: Job? = null
    /** Unlocks the packs if the same-tab fallback never navigates away. */
    private var redirectGraceJob: Job? = null
    var bookHistory by mutableStateOf<List<BookHistoryItem>>(emptyList())
        private set
    /** True once `loadUserInfo()` has fetched book history at least once,
     *  so the UI can distinguish "still loading" from "really empty". */
    var bookHistoryLoaded by mutableStateOf(false)
        private set
    var debugTopUp by mutableStateOf(false)
        private set

    // Redeem flow
    var redeemCodeInput by mutableStateOf("")
    var redeemInFlight by mutableStateOf(false)
        private set
    var redeemError by mutableStateOf<String?>(null)
        private set
    var redeemSuccessAdded by mutableStateOf<Int?>(null)
        private set

    // EULA consent — only relevant for anonymous uploads. Once accepted it is
    // persisted in localStorage; the checkbox is then hidden on future visits.
    var eulaAccepted by mutableStateOf(AppDataStore.isEulaAccepted())
        private set
    var eulaChecked by mutableStateOf(false)
    var eulaError by mutableStateOf(false)
        private set

    fun setEulaChecked(value: Boolean) {
        eulaChecked = value
        if (value) eulaError = false
    }

    init {
        // Synchronous stale-while-revalidate: hydrate from the local
        // user cache BEFORE the bootstrap coroutine starts. The first
        // paint then runs against the last-known-good values rather
        // than the type defaults (credits=0, empty history, null user
        // name), which is what produced the flicker on every reload.
        // The async chain below re-fetches the real numbers from /me
        // and /history; on arrival they overwrite the cached values
        // and Compose recomposes only the cells that actually changed.
        hydrateFromCache()
        // In the payment window, back from the provider: the tab that
        // opened it shows the outcome, so this one reports in and closes.
        if (handBackToOpener()) {
            // Normally gone by now; if the browser kept the window open,
            // carry on as an ordinary tab.
            kotlinx.browser.window.setTimeout({ bootstrap() }, 1000)
        } else {
            bootstrap()
        }
    }

    private fun bootstrap() {
        // A former payment window carrying on as an ordinary tab drops the
        // name, or the next checkout's window.open would return this tab.
        if (kotlinx.browser.window.name == CHECKOUT_WINDOW_NAME) kotlinx.browser.window.name = ""
        installPaymentReturnListeners()
        viewModelScope.launch {
            handleAuthCallback()
            loadUserInfo()
            // Own coroutine so the claim round-trip doesn't hold up
            // the rest of the bootstrap.
            launch { reportPendingPurchases() }
            loadPricing()
            // After loadUserInfo — /payments/{id}/status is owner-checked —
            // and loadPricing (the revenue value), but before the book
            // restore, so someone back from the payment page sees the
            // outcome straight away.
            resumePendingPayment()
            restoreAnalysisState()
            restoreCurrentBookFromHistory()
        }
    }

    /**
     * Pull whatever's in [UserCache] into this VM. No-op if:
     *  - we're anonymous (no cache key)
     *  - the cached `userId` doesn't match the current JWT subject
     *    (different user signed in on this browser since the snapshot
     *    was written — [UserCache.load] enforces this)
     *  - nothing's ever been cached on this browser
     *
     * Idempotent and pure-read; safe to call at construction time.
     */
    private fun hydrateFromCache() {
        val snapshot = UserCache.load() ?: return
        userName = snapshot.email
        credits = snapshot.credits
        freeShortRemaining = snapshot.freeShortRemaining
        freeShortCharThreshold = snapshot.freeShortCharThreshold
        freeAnnotationsRemaining = snapshot.freeAnnotationsRemaining
        freeCoverBatchesRemaining = snapshot.freeCoverBatchesRemaining
        freeTeaserPreviewsRemaining = snapshot.freeTeaserPreviewsRemaining
        debugTopUp = snapshot.debugTopUp
        emailVerified = snapshot.emailVerified
        canEditText = snapshot.canEditText
        bookHistory = snapshot.bookHistory
        // `bookHistoryLoaded = true` looks tempting here, but it would
        // lie: the cached list might be stale (book deleted on another
        // device, etc.). We leave the flag false until the real /history
        // arrives — the UI still shows the cached list while the
        // background fetch is in flight, and HistoryEmptyState only
        // appears AFTER the fresh data confirms it really is empty.

        // Rehydrate the last-opened book so a plain refresh on the
        // results page paints ResultsArea on the first frame instead
        // of flashing UploadArea → LoadingCard → ResultsArea. The
        // backend probe below (in `restoreCurrentBookFromHistory`)
        // still fires so a server-side change to the book is picked
        // up — Compose just recomposes the cells that actually differ.
        val savedId = snapshot.currentBookId
        val savedName = snapshot.currentFileName
        val savedJson = snapshot.currentAnalysisJson
        if (savedId != null && savedName != null && savedJson != null) {
            val parsed = runCatching {
                BookRepository.parseAnalysisJson(savedJson)
            }.getOrNull()
            if (parsed != null) {
                bookId = savedId
                pickedFileName = savedName
                analysisResult = parsed
                analysisState = AnalysisState.Done
                currentAnalysisJson = savedJson
                // Pre-set AI state from cached history so the CTA button is
                // disabled during the loadUserInfo round-trip. Prevents a
                // click in that window from accidentally re-charging credits
                // if the analysis finished just before the fresh status arrives.
                val cachedItem = snapshot.bookHistory.firstOrNull { it.fingerprint == savedId }
                if (cachedItem?.aiStatus == "running") {
                    aiState = AnalysisState.Running
                    aiStageName = "Resuming…"
                }
                // Pre-set promo loading flags from cache so generate buttons
                // are disabled during the reattachPromoJobs round-trip.
                if (snapshot.annotationRunning) annotationLoading = true
                if (snapshot.coverBatchRunning) coverLoading = setOf(1, 2, 3)
                if (snapshot.teaserRunning) teaserLoadingKind = TeaserLoadingKind.Preview
            }
        }
    }

    /**
     * Persist the current user-scoped UI state to [UserCache] so the
     * next page load can paint it instantly. Called from [loadUserInfo]
     * after every fresh /me + /history pair; cheap (single localStorage
     * write of ~few KB), so no debounce needed.
     *
     * Only runs when we have a confirmed user id — the cache key
     * depends on it.
     */
    private fun persistCache() {
        val userId = AppDataStore.getUserId() ?: return
        // Only snapshot the open book if all three slots are populated
        // AND the static analysis is actually done. Mid-load states
        // (Running, Idle, Error) deliberately write null book fields so
        // a refresh from those states doesn't try to rehydrate a
        // half-baked record.
        val snapshotBookId: String?
        val snapshotFileName: String?
        val snapshotJson: String?
        if (analysisState == AnalysisState.Done &&
            bookId != null && pickedFileName != null && currentAnalysisJson != null
        ) {
            snapshotBookId = bookId
            snapshotFileName = pickedFileName
            snapshotJson = currentAnalysisJson
        } else {
            snapshotBookId = null
            snapshotFileName = null
            snapshotJson = null
        }
        UserCache.save(UserCacheSnapshot(
            userId = userId,
            email = userName,
            credits = credits,
            freeShortRemaining = freeShortRemaining,
            freeShortCharThreshold = freeShortCharThreshold,
            freeAnnotationsRemaining = freeAnnotationsRemaining,
            freeCoverBatchesRemaining = freeCoverBatchesRemaining,
            freeTeaserPreviewsRemaining = freeTeaserPreviewsRemaining,
            debugTopUp = debugTopUp,
            emailVerified = emailVerified,
            canEditText = canEditText,
            bookHistory = bookHistory,
            currentBookId = snapshotBookId,
            currentFileName = snapshotFileName,
            currentAnalysisJson = snapshotJson,
            annotationRunning = annotationLoading,
            coverBatchRunning = promoCoroutineJobs.containsKey(promoKey("COVER_BATCH")),
            teaserRunning = teaserLoading,
            savedAtMs = kotlin.js.Date.now(),
        ))
    }

    /**
     * Bootstrap-time pricing fetch. Failure is non-fatal — Pricing()
     * defaults render until the next page-load retries the call.
     */
    private suspend fun loadPricing() {
        try {
            pricing = BookRepository.getPricing()
        } catch (e: kotlinx.coroutines.CancellationException) {
            // Re-throw so the coroutine is genuinely cancelled.
            // Without this `catch (Exception)` would swallow it,
            // letting the next-line state write happen on the new
            // book's screen after a switch — visible as a stray
            // error toast labelled with the OLD book's failure.
            throw e
        } catch (e: Exception) {
            console.warn("Pricing fetch failed: ${e.message}; using client-side defaults")
        }
    }

    /**
     * Plain page refresh recovery. The sign-in handoff path
     * (`restoreAnalysisState`) only fires when the user clicked Sign in
     * with a book in hand; this one rehydrates the *last open* book on
     * any reload, by replaying the same flow as clicking the history row.
     *
     * Logged-in only — anon users don't have a server-side history to
     * resolve the fingerprint against. We also defer to
     * `restoreAnalysisState` if it already populated state, so the
     * sign-in handoff (which carries a freshly produced analysis JSON)
     * wins over re-fetching the same book by id.
     */
    private suspend fun restoreCurrentBookFromHistory() {
        if (!isLoggedIn) return
        val saved = AppDataStore.getCurrentBookId() ?: return
        val item = bookHistory.firstOrNull { it.fingerprint == saved }
        if (item == null) {
            // Saved id no longer in history (deleted, or different user
            // logged in) — drop the stale pointer so we don't churn.
            AppDataStore.clearCurrentBookId()
            return
        }
        if (bookId != null) {
            // Static analysis was already painted by hydrateFromCache — skip
            // the full selectFromHistory (which would clear + re-fetch it) to
            // avoid a flash. But still re-attach to the AI pipeline and promo
            // jobs, which are NOT stored in the cache snapshot.
            reattachPromoJobs(item.fingerprint)
            // Always load completed promo results regardless of AI status —
            // e.g. covers can be done while a deep-analysis rerun is running.
            // Skip if reattachPromoJobs already owns the state (avoids stale
            // overwrite racing against the active polling loop).
            if (!annotationLoading) loadAnnotation()
            if (coverLoading.isEmpty()) loadCovers()
            if (!teaserLoading) loadTeaser()
            when (item.aiStatus) {
                "running" -> {
                    val jobId = item.aiJobId
                    if (jobId != null) {
                        aiState = AnalysisState.Running
                        aiStageName = "Resuming…"
                        // Track the resume Job so a subsequent book switch
                        // can cancel it via clearPerBookData; otherwise the
                        // polling loop would survive and rewrite the next
                        // book's aiReport / aiState on completion.
                        aiPipelineJob?.cancel()
                        aiPipelineJob = viewModelScope.launch {
                            resumeRunningPipeline(jobId, item.fingerprint)
                        }
                    }
                }
                else -> loadPaidAiReport(item.fingerprint)
            }
            return
        }
        selectFromHistory(item)
    }

    private suspend fun handleAuthCallback() {
        try {
            if (Auth.handleCallback()) {
                isLoggedIn = true
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            // Re-throw so the coroutine is genuinely cancelled.
            // Without this `catch (Exception)` would swallow it,
            // letting the next-line state write happen on the new
            // book's screen after a switch — visible as a stray
            // error toast labelled with the OLD book's failure.
            throw e
        } catch (e: Exception) {
            errorMessage = "Auth failed: ${e.message}"
        }
    }

    /**
     * Refresh credits + free-quota counters from /me. Cheap one
     * roundtrip; called after each promo-generation success so the
     * Cover/Teaser CTAs immediately reflect new "free remaining"
     * numbers and the credit balance updates without a page reload.
     */
    private suspend fun refreshUserCounters() {
        val u = BookRepository.fetchUserInfo() ?: return
        credits = u.credits
        freeShortRemaining = u.freeShortRemaining
        freeAnnotationsRemaining = u.freeAnnotationsRemaining
        freeCoverBatchesRemaining = u.freeCoverBatchesRemaining
        freeTeaserPreviewsRemaining = u.freeTeaserPreviewsRemaining
        emailVerified = u.emailVerified
        canEditText = u.canEditText
        persistCache()
    }

    private suspend fun loadUserInfo() {
        if (!Auth.isLoggedIn) {
            // Cache is only valid for the logged-in user it was written
            // for; if the user is now anon (token revoked / logged out
            // in another tab), drop any stale snapshot so it doesn't
            // leak into the next session.
            UserCache.clear()
            AppDataStore.clearUserId()
            // Implementation tracks the last-bound id internally — a
            // no-op call when we were already anonymous, an unbind
            // (and the matching cleanup of any `login` gate state)
            // when we were authenticated.
            analytics.unbindUser()
            return
        }
        isLoggedIn = true
        val user = BookRepository.fetchUserInfo()
        // Pin the server-side user id as soon as /me confirms it. This
        // is what UserCache and persistCache key off — must be set
        // BEFORE the persistCache call below.
        if (user != null && user.id.isNotBlank()) {
            AppDataStore.setUserId(user.id)
        }
        userName = user?.email ?: user?.name
        credits = user?.credits ?: 0
        debugTopUp = user?.debugTopUp ?: false
        freeShortRemaining = user?.freeShortRemaining ?: 0
        freeShortCharThreshold = user?.freeShortCharThreshold ?: 30000
        freeAnnotationsRemaining = user?.freeAnnotationsRemaining ?: 0
        freeCoverBatchesRemaining = user?.freeCoverBatchesRemaining ?: 0
        freeTeaserPreviewsRemaining = user?.freeTeaserPreviewsRemaining ?: 0
        emailVerified = user?.emailVerified ?: false
        canEditText = user?.canEditText ?: false
        if (!emailVerified) {
            // Silently re-check in case the user just returned from the
            // verification email link — refresh gets a token with the updated claim.
            val refreshed = Auth.refreshAccessToken()
            if (refreshed != null) {
                resetAuthCache()
                val freshUser = BookRepository.fetchUserInfo()
                emailVerified = freshUser?.emailVerified ?: false
            }
            if (!emailVerified) applySentAtCooldown(user?.verificationEmailSentAt)
        }
        bookHistory = BookRepository.getBookHistory()
        bookHistoryLoaded = true
        // Snapshot the fresh state so the next page load can paint it
        // immediately — but ONLY when /me actually returned data. A
        // transient network failure zeros out the VM fields above
        // (`credits = user?.credits ?: 0`); writing those zeros to
        // the snapshot would clobber the previously-cached good
        // values and the user would see a fake "0 credits" on the
        // next reload until /me succeeds. Better to leave whatever
        // snapshot is already on disk — that's the stale-while-
        // revalidate contract anyway.
        if (user != null) persistCache()
        // Analytics user-bind + property refresh. `login` fires only
        // when the resolved user id actually changes (new sign-in or
        // account swap on the same browser) — repeated /me polls don't
        // re-emit the event. User properties refresh every time so
        // segments stay in sync with current credit / verification
        // state.
        if (user != null && user.id.isNotBlank()) {
            analytics.bindUser(
                userId = user.id,
                credits = user.credits,
                emailVerified = user.emailVerified,
                freeShortRemaining = user.freeShortRemaining,
                locale = currentLocaleTag(),
            )
        }
    }

    fun sendVerificationEmail() {
        analytics.emailVerificationResendClicked()
        val s = if (appLocaleSnapshot() == "ru") RuStrings else EnStrings
        viewModelScope.launch {
            when (val result = BookRepository.sendVerificationEmail()) {
                is VerificationEmailResult.Sent -> {
                    Toaster.show(s.verificationEmailSent, ToastKind.Success)
                    startCooldown(60)
                }
                is VerificationEmailResult.AlreadyVerified -> {
                    emailVerified = true
                    Toaster.show(s.verificationAlreadyDone, ToastKind.Info)
                }
                is VerificationEmailResult.Cooldown ->
                    startCooldown(result.retryAfterSeconds.toInt())
                is VerificationEmailResult.Error ->
                    Toaster.show(s.verificationEmailError, ToastKind.Error)
            }
        }
    }

    private fun startCooldown(seconds: Int) {
        cooldownJob?.cancel()
        verificationCooldownSeconds = seconds
        cooldownJob = viewModelScope.launch {
            while (verificationCooldownSeconds > 0) {
                delay(1000)
                verificationCooldownSeconds--
            }
        }
    }

    private fun applySentAtCooldown(sentAtEpochSecond: Long?) {
        sentAtEpochSecond ?: return
        val nowSeconds = (js("Date.now()").unsafeCast<Double>() / 1000).toLong()
        val elapsed = nowSeconds - sentAtEpochSecond
        val remaining = (60 - elapsed).toInt()
        if (remaining > 0) startCooldown(remaining)
    }

    fun confirmVerification() {
        analytics.emailVerificationConfirmClicked()
        val s = if (appLocaleSnapshot() == "ru") RuStrings else EnStrings
        viewModelScope.launch {
            Auth.refreshAccessToken() ?: run {
                Toaster.show(s.verificationNotYet, ToastKind.Info)
                return@launch
            }
            // Clear Ktor's internal bearer-token cache so fetchUserInfo()
            // picks up the freshly-minted token, not the stale cached one.
            resetAuthCache()
            val user = BookRepository.fetchUserInfo()
            emailVerified = user?.emailVerified ?: false
            if (!emailVerified) Toaster.show(s.verificationNotYet, ToastKind.Info)
        }
    }

    private fun requireEmailVerified(): Boolean {
        if (emailVerified) return true
        val s = if (appLocaleSnapshot() == "ru") RuStrings else EnStrings
        showNudge(s.verificationRequired)
        return false
    }

    /**
     * Re-runs the most recent failed analysis. Used by the retry button on
     * `ErrorCard`. Currently we don't keep the original [File] around (the
     * picker doesn't survive a recomposition) — so the simplest meaningful
     * retry is to clear the error state and let the user pick again.
     */
    fun clearError() {
        errorMessage = null
        if (analysisState == AnalysisState.Error) analysisState = AnalysisState.Idle
        if (aiState == AnalysisState.Error) aiState = AnalysisState.Idle
    }

    private suspend fun restoreAnalysisState() {
        val savedBookId = Auth.restoreBookId()
        val savedFileName = Auth.restoreFileName()
        val savedAnalysis = Auth.restoreAnalysis()
        if (savedBookId != null && savedFileName != null && savedAnalysis != null) {
            Auth.clearSavedState()
            bookId = savedBookId
            AppDataStore.setCurrentBookId(savedBookId)
            pickedFileName = savedFileName
            try {
                analysisResult = BookRepository.parseAnalysisJson(savedAnalysis)
                analysisState = AnalysisState.Done
                currentAnalysisJson = savedAnalysis
            } catch (_: Exception) {}
            // Anon → authenticated handoff: ask the backend to copy
            // the per-fingerprint anon files into this user's own
            // dir. Without this the user would have to re-upload to
            // run promo / AI on a book they already analysed as anon.
            // Idempotent and silent on no-op, safe to call always.
            if (isLoggedIn) {
                BookRepository.claimAnonBook(savedBookId)
            }
        }
    }

    /**
     * Clears every piece of book-scoped state on the VM. Called wherever
     * we switch contexts to a different book (or to none) so promo /
     * annotation / AI cached on the previous book don't leak into the
     * new view.
     */
    private fun clearPerBookData() {
        // Stop any local polling first so a coroutine that's still
        // mid-`finally` block can't write the OLD book's loading-false
        // flag (or worse, an old result) into the new book's VM state.
        // The backend jobs themselves stay alive in PromoJobRegistry;
        // `selectFromHistory` rediscovers and re-attaches them via
        // [reattachPromoJobs].
        promoCoroutineJobs.values.forEach { it.cancel() }
        promoCoroutineJobs.clear()
        aiPipelineJob?.cancel()
        aiPipelineJob = null
        analysisResult = null
        currentAnalysisJson = null
        aiState = AnalysisState.Idle
        aiReport = null
        aiStageName = ""
        annotationResult = null
        annotationLoading = false
        annotationError = null
        coverResults = emptyMap()
        coverLoading = emptySet()
        coverError = null
        teaserResult = null
        teaserLoadingKind = null
        teaserError = null
    }

    fun reset() {
        if (bookId != null || analysisState != AnalysisState.Idle) {
            analytics.staticAnalysisReplaced()
        }
        pickedFileName = null
        bookId = null
        analysisState = AnalysisState.Idle
        errorMessage = null
        clearPerBookData()
        AppDataStore.clearCurrentBookId()
        // Refresh in case a pipeline finished in the background while the
        // user was away from the picker.
        if (isLoggedIn) {
            viewModelScope.launch { refreshHistory() }
        }
    }

    /**
     * Re-fetch the book history from the server and update the local
     * cache. Centralises «list might have changed, write the new
     * snapshot» so individual call sites (pipeline finish, book delete,
     * AI completion, …) don't all have to remember to call
     * [persistCache] themselves.
     */
    private suspend fun refreshHistory() {
        if (!isLoggedIn) return
        bookHistory = BookRepository.getBookHistory()
        bookHistoryLoaded = true
        persistCache()
    }

    fun pickFile() {
        // Re-entry guard: an analysis in flight owns the picker — block
        // a second upload from running its `consumePickedFile` concurrently.
        // The UI hides UploadArea in Running, but defence-in-depth: the
        // event could still fire via drag-and-drop on a stale view, or
        // before the next recomposition lands.
        if (analysisState == AnalysisState.Running) return
        // Anonymous users must accept the EULA before the file dialog opens.
        // Once accepted, the flag is persisted and we never gate again.
        if (!gateEulaForPick()) return

        analytics.fileUploadStarted(AnalyticsEvents.UploadSource.PICKER)
        viewModelScope.launch {
            // Restrict to the formats the backend converter supports —
            // also keeps Android Chrome from defaulting to the photo
            // picker when `accept` is unset on the underlying <input>.
            val file = pickFile(
                extensions = listOf("fb2", "epub", "txt", "html", "htm", "docx", "pdf"),
            ) ?: return@launch
            consumePickedFile(file)
        }
    }

    /**
     * Drag-and-drop entry point. Same gating + analysis pipeline as
     * [pickFile], but the [File] is supplied by the caller (drop event).
     */
    fun pickFileFromDrop(file: org.w3c.files.File) {
        if (analysisState == AnalysisState.Running) return
        if (!gateEulaForPick()) return
        analytics.fileUploadStarted(AnalyticsEvents.UploadSource.DROP)
        viewModelScope.launch { consumePickedFile(file) }
    }

    private fun gateEulaForPick(): Boolean {
        if (!isLoggedIn && !eulaAccepted) {
            if (!eulaChecked) {
                eulaError = true
                return false
            }
            AppDataStore.acceptEula()
            eulaAccepted = true
            analytics.eulaAccepted()
        }
        return true
    }

    private suspend fun consumePickedFile(file: org.w3c.files.File) {
        try {
            pickedFileName = file.name
            analysisState = AnalysisState.Running
            errorMessage = null
            // Drop everything that was tied to the previous book — without
            // this, AI / promo state from the prior session leaks into the
            // new book's view until those endpoints (re)load.
            clearPerBookData()

            val bytes = file.readBytesSuspend()

            val (result, id) = BookRepository.analyzeFile(bytes, file.name)
            bookId = id
            AppDataStore.setCurrentBookId(id)
            analysisResult = result
            analysisState = AnalysisState.Done
            // BookRepository writes the raw JSON to `lastAnalysisJson`
            // as part of `analyzeFile` — mirror it here so persistCache
            // has the snapshot to write.
            currentAnalysisJson = BookRepository.lastAnalysisJson
            analytics.fileUploadCompleted(
                words = result.words,
                language = result.language?.name?.lowercase() ?: "unknown",
                qualityScore = result.quality,
            )
            refreshHistory()
        } catch (e: kotlinx.coroutines.CancellationException) {
            // Re-throw so the coroutine is genuinely cancelled.
            // Without this `catch (Exception)` would swallow it,
            // letting the next-line state write happen on the new
            // book's screen after a switch — visible as a stray
            // error toast labelled with the OLD book's failure.
            throw e
        } catch (e: Exception) {
            errorMessage = e.message ?: "Analysis failed"
            analysisState = AnalysisState.Error
            analytics.fileUploadFailed(e.message)
        }
    }

    fun runAiAnalysis() {
        if (!requireEmailVerified()) return
        val id = bookId ?: return
        // Re-entry guard: a fast double-click on the CTA can fire two
        // event handlers before the UI re-renders the running state and
        // hides the button. Without this, both calls launch a coroutine
        // that polls the same backend job — the backend's RUNNING
        // short-circuit blocks the re-charge, but the duplicate polling
        // / state churn on the frontend is messy. Other generate-*
        // functions in this VM follow the same pattern (annotationLoading,
        // coverLoading, teaserLoading guards).
        if (aiState == AnalysisState.Running) return
        // Re-run vs first run — the UI route is the same callsite, so
        // distinguish via state: `Done` with an existing report ⇒ user
        // explicitly pressed the rerun button. Useful for measuring how
        // often the AI output prompts a second attempt.
        val isRerun = aiState == AnalysisState.Done && aiReport != null
        if (isRerun) analytics.aiRerunClicked()
        val wordCount = analysisResult?.words ?: 0
        val creditsBefore = credits
        val startedAt = kotlinx.browser.window.performance.now()
        analytics.aiAnalysisStarted(wordCount = wordCount, isRerun = isRerun)
        aiState = AnalysisState.Running
        aiStageName = "Starting..."
        aiStage = 0
        aiChunk = 0
        aiTotalChunks = 0
        aiEtaSeconds = null
        aiElapsedSeconds = 0

        aiPipelineJob?.cancel()
        aiPipelineJob = viewModelScope.launch {
            try {
                val result = BookRepository.runPipeline(
                    id,
                    onStarted = { remaining ->
                        // Backend deducts atomically inside /pipeline/start.
                        // Apply the new balance to the UI immediately so the
                        // chip reflects the charge on click, not on completion.
                        if (remaining != null && bookId == id) credits = remaining
                    },
                ) { status ->
                    // Drop status updates for a book the user has already
                    // navigated away from — otherwise the new book's UI
                    // would flash the old book's progress numbers until the
                    // coroutine notices it was cancelled.
                    if (bookId != id) return@runPipeline
                    aiStage = status.stage
                    aiTotalStages = status.total_stages
                    aiStageName = status.stage_name
                    aiChunk = status.chunk
                    aiTotalChunks = status.total_chunks
                    aiEtaSeconds = status.eta_seconds
                    aiElapsedSeconds = status.elapsed_seconds
                }
                if (bookId == id) {
                    aiReport = result.report
                    if (result.remainingCredits != null) credits = result.remainingCredits
                    aiState = AnalysisState.Done
                }
                val durationSec = ((kotlinx.browser.window.performance.now() - startedAt) / 1000.0).toInt()
                val finalScore = result.report.score.final
                // Skip analytics when remainingCredits is absent — the
                // null-fallback to creditsBefore would make costCharged=0
                // and misreport every paid run as free.
                if (result.remainingCredits != null) {
                    val costCharged = (creditsBefore - result.remainingCredits)
                        .coerceAtLeast(0)
                    analytics.aiAnalysisCompleted(
                        durationSeconds = durationSec,
                        finalScore = finalScore,
                        isFree = costCharged == 0,
                        costCredits = costCharged,
                    )
                }
                // Refresh free-short counter (server-side daily reset).
                BookRepository.fetchUserInfo()?.let { u ->
                    freeShortRemaining = u.freeShortRemaining
                    credits = u.credits
                }
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                val msg = e.message ?: ""
                // Legacy book whose source text was pruned — pipeline can't
                // run and never will. Mirror the static-cache cleanup path:
                // silently reset to picker and refetch history (the /history
                // filter then drops the orphan row).
                if (msg.contains("Book not found") && bookId == id) {
                    pickedFileName = null
                    bookId = null
                    analysisState = AnalysisState.Idle
                    aiState = AnalysisState.Idle
                    AppDataStore.clearCurrentBookId()
                    clearPerBookData()
                    refreshHistory()
                } else if (bookId == id) {
                    errorMessage = "AI analysis failed: $msg"
                    aiState = AnalysisState.Error
                }
                analytics.aiAnalysisFailed(reason = msg, stage = aiStage)
                // Backend refunds on pipeline failure via refundAndFail.
                // Re-fetch /me so the UI picks up the restored balance
                // instead of stranding the user on the post-deduction view.
                refreshUserCounters()
            }
            // Refresh history so the just-finished book gets its AI badge
            // and `hasAiResult` flips to true on the next navigation back.
            refreshHistory()
        }
    }

    /**
     * Kick off (or regenerate) the promo annotation. Synchronous HTTP
     * call to the backend — the spinner stays up the whole time. We
     * don't pre-check on book open; the card calls `loadAnnotation()`
     * itself if it wants to surface a cached result.
     */
    fun generateAnnotation() {
        if (!requireEmailVerified()) return
        val id = bookId ?: return
        if (annotationLoading) return
        annotationLoading = true
        annotationError = null
        val key = promoKey("ANNOTATION")
        val startedAt = kotlinx.browser.window.performance.now()
        analytics.promoGenerateStarted(AnalyticsEvents.PromoKind.ANNOTATION)
        promoCoroutineJobs[key] = viewModelScope.launch {
            val job = coroutineContext[kotlinx.coroutines.Job]
            try {
                annotationResult = BookRepository.generateAnnotation(id)
                refreshUserCounters()
                val dur = ((kotlinx.browser.window.performance.now() - startedAt) / 1000.0).toInt()
                analytics.promoGenerateCompleted(AnalyticsEvents.PromoKind.ANNOTATION, dur)
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                annotationError = e.message ?: "Annotation failed"
                analytics.promoGenerateFailed(AnalyticsEvents.PromoKind.ANNOTATION, e.message)
            } finally {
                annotationLoading = false
                promoCoroutineJobs.remove(key)
                if (job?.isActive == true) persistCache()
            }
        }
        persistCache()
    }

    /**
     * Try to fetch a previously generated annotation from the backend.
     * Used on book open so the card starts in Generated state if there's
     * a cached result on disk. Silent on 404 (no annotation yet).
     */
    fun loadAnnotation() {
        val id = bookId ?: return
        viewModelScope.launch {
            try {
                annotationResult = BookRepository.getAnnotation(id)
            } catch (_: Exception) {
                // soft-fail — UI just stays in Idle
            }
        }
    }

    /**
     * Master action: generate all three cover variants sequentially.
     * First call regenerates the cover_prompt stage; subsequent ones
     * reuse it so we don't pay for the same prompts three times.
     * Each result populates its slot as soon as the request returns,
     * so the UI progressively fills.
     */
    fun generateAllCovers(req: CoverRequest) {
        if (!requireEmailVerified()) return
        val id = bookId ?: return
        if (req.title.isBlank()) {
            coverError = "Введите название книги"
            return
        }
        if (coverLoading.isNotEmpty()) return
        coverError = null
        // Wipe previous results so the UI clearly shows "regenerating
        // everything" — three placeholders during the atomic call.
        coverResults = emptyMap()
        coverLoading = setOf(1, 2, 3)
        val key = promoKey("COVER_BATCH")
        val startedAt = kotlinx.browser.window.performance.now()
        analytics.promoGenerateStarted(AnalyticsEvents.PromoKind.COVER)
        promoCoroutineJobs[key] = viewModelScope.launch {
            val pollJob = launch { pollCoversWhileBatchRuns(id) }
            try {
                val list = BookRepository.generateCoverBatch(id, req)
                coverResults = list.associateBy { it.variant }
                val dur = ((kotlinx.browser.window.performance.now() - startedAt) / 1000.0).toInt()
                analytics.promoGenerateCompleted(AnalyticsEvents.PromoKind.COVER, dur)
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                coverError = e.message ?: "Cover batch failed"
                analytics.promoGenerateFailed(AnalyticsEvents.PromoKind.COVER, e.message)
            } finally {
                pollJob.cancel()
                coverLoading = emptySet()
                promoCoroutineJobs.remove(key)
                refreshUserCounters()
            }
        }
        persistCache()
    }

    /**
     * Sidecar polling that re-reads `/promo/cover/{bookId}` every few
     * seconds while a cover batch is running. The backend writes each
     * leg to disk as soon as it lands; this poll surfaces them
     * progressively so the user sees covers fill in slot-by-slot
     * instead of three placeholders for the full ~90 sec batch.
     *
     * Lifted out of the start-coroutine body because the re-attach
     * path ([reattachCoverBatch]) needs the same sidecar.
     */
    private suspend fun pollCoversWhileBatchRuns(id: String) {
        while (true) {
            delay(4000)
            val list = try { BookRepository.getCovers(id) } catch (_: Exception) { null }
            if (list != null && list.isNotEmpty()) {
                coverResults = list.associateBy { it.variant }
                coverLoading = (1..3).filter { it !in coverResults }.toSet()
            }
        }
    }

    /**
     * Per-variant regeneration. Server-side reuses the existing
     * cover_prompt.json and only re-renders the chosen variant.
     * Always charges COVER_REGEN_COST — no interaction with the
     * free-batch quota.
     */
    fun regenerateCover(variant: Int, req: CoverRequest) {
        if (!requireEmailVerified()) return
        val id = bookId ?: return
        if (req.title.isBlank()) {
            coverError = "Введите название книги"
            return
        }
        if (variant in coverLoading) return
        coverError = null
        coverLoading = coverLoading + variant
        val key = promoKey("COVER_REGEN", variant)
        analytics.promoGenerateStarted(
            AnalyticsEvents.PromoKind.COVER_REGEN,
            extra = mapOf("variant" to variant),
        )
        val startedAt = kotlinx.browser.window.performance.now()
        promoCoroutineJobs[key] = viewModelScope.launch {
            try {
                val res = BookRepository.regenerateCoverVariant(id, variant, req)
                coverResults = coverResults + (variant to res)
                refreshUserCounters()
                val dur = ((kotlinx.browser.window.performance.now() - startedAt) / 1000.0).toInt()
                analytics.promoGenerateCompleted(AnalyticsEvents.PromoKind.COVER_REGEN, dur)
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                coverError = e.message ?: "Cover #$variant failed"
                analytics.promoGenerateFailed(
                    AnalyticsEvents.PromoKind.COVER_REGEN,
                    e.message,
                    extra = mapOf("variant" to variant),
                )
            } finally {
                coverLoading = coverLoading - variant
                promoCoroutineJobs.remove(key)
            }
        }
        persistCache()
    }

    /**
     * Ask the backend «what promo generations are still running for
     * this book?» and re-subscribe a local polling coroutine to each.
     * Called from [selectFromHistory] right after the static analysis
     * loads. The corresponding loader (`annotationLoading=true`,
     * `coverLoading={1,2,3}` etc.) is flipped synchronously so the UI
     * shows the spinner on the very first frame after switch-back —
     * not after the next poll tick lands.
     *
     * The backend job survives book switches in [PromoJobRegistry];
     * cancelling our local coroutine in [clearPerBookData] only
     * disconnects our subscription, never the work.
     */
    private fun reattachPromoJobs(id: String) {
        viewModelScope.launch {
            val active = BookRepository.getActivePromoJobs(id)
            val kinds = active.mapNotNull { it.kind }.toSet()
            for (job in active) {
                val jobId = job.jobId ?: continue
                when (job.kind) {
                    "ANNOTATION" -> reattachAnnotation(jobId)
                    "COVER_BATCH" -> reattachCoverBatch(id, jobId)
                    "COVER_REGEN" -> {
                        val variant = job.variant ?: continue
                        reattachCoverRegen(jobId, variant)
                    }
                    "TEASER" -> reattachTeaser(jobId)
                }
            }
            // Reset any loading flags that were pre-set from cache but the
            // backend didn't confirm a matching active job — covers the case
            // where the backend restarted and the registry was wiped, or the
            // job finished between the cache write and this page load.
            // Also trigger a load so results that landed while we were away
            // are surfaced immediately (restoreCurrentBookFromHistory skipped
            // them because the loading flag was set at the time).
            // Cover check spans both COVER_BATCH and COVER_REGEN — reattachCoverRegen
            // sets coverLoading synchronously before this line runs, so we must not
            // wipe it if any cover job (batch or per-slot regen) was re-attached.
            val hasCoverJob = kinds.any { it.startsWith("COVER") }
            var flagsCleared = false
            if ("ANNOTATION" !in kinds && annotationLoading) {
                annotationLoading = false
                loadAnnotation()
                flagsCleared = true
            }
            if (!hasCoverJob && coverLoading.isNotEmpty()) {
                coverLoading = emptySet()
                loadCovers()
                flagsCleared = true
            }
            if ("TEASER" !in kinds && teaserLoading) {
                teaserLoadingKind = null
                loadTeaser()
                flagsCleared = true
            }
            if (flagsCleared) persistCache()
        }
    }

    private fun reattachAnnotation(jobId: String) {
        annotationLoading = true
        annotationError = null
        val key = promoKey("ANNOTATION")
        promoCoroutineJobs[key] = viewModelScope.launch {
            val job = coroutineContext[kotlinx.coroutines.Job]
            try {
                annotationResult = BookRepository.awaitAnnotationJob(jobId)
                refreshUserCounters()
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                annotationError = e.message ?: "Annotation failed"
            } finally {
                annotationLoading = false
                promoCoroutineJobs.remove(key)
                if (job?.isActive == true) persistCache()
            }
        }
    }

    private fun reattachCoverBatch(id: String, jobId: String) {
        coverError = null
        coverResults = emptyMap()
        coverLoading = setOf(1, 2, 3)
        val key = promoKey("COVER_BATCH")
        promoCoroutineJobs[key] = viewModelScope.launch {
            val pollJob = launch { pollCoversWhileBatchRuns(id) }
            try {
                val list = BookRepository.awaitCoverBatchJob(jobId)
                coverResults = list.associateBy { it.variant }
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                coverError = e.message ?: "Cover batch failed"
            } finally {
                pollJob.cancel()
                coverLoading = emptySet()
                promoCoroutineJobs.remove(key)
                refreshUserCounters()
            }
        }
    }

    private fun reattachCoverRegen(jobId: String, variant: Int) {
        coverError = null
        coverLoading = coverLoading + variant
        val key = promoKey("COVER_REGEN", variant)
        promoCoroutineJobs[key] = viewModelScope.launch {
            try {
                val res = BookRepository.awaitCoverRegenJob(jobId)
                coverResults = coverResults + (variant to res)
                refreshUserCounters()
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                coverError = e.message ?: "Cover #$variant failed"
            } finally {
                coverLoading = coverLoading - variant
                promoCoroutineJobs.remove(key)
            }
        }
    }

    /**
     * Teaser re-attach can't distinguish Preview / Video / Regenerate
     * sub-modes from the job dto alone — that detail is in the
     * request body the user originally posted, not echoed back by
     * the registry. We surface a generic [TeaserLoadingKind.Preview]
     * spinner; visually all three carry the same loading affordance.
     */
    private fun reattachTeaser(jobId: String) {
        teaserError = null
        teaserLoadingKind = TeaserLoadingKind.Preview
        val key = promoKey("TEASER")
        promoCoroutineJobs[key] = viewModelScope.launch {
            val job = coroutineContext[kotlinx.coroutines.Job]
            try {
                teaserResult = BookRepository.awaitTeaserJob(jobId)
                refreshUserCounters()
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                teaserError = e.message ?: "Teaser generation failed"
            } finally {
                teaserLoadingKind = null
                promoCoroutineJobs.remove(key)
                if (job?.isActive == true) persistCache()
            }
        }
    }

    /** Fetch every cached cover on book open. Silent on errors. */
    fun loadCovers() {
        val id = bookId ?: return
        viewModelScope.launch {
            try {
                val list = BookRepository.getCovers(id)
                coverResults = list.associateBy { it.variant }
            } catch (_: Exception) {
                // soft-fail
            }
        }
    }

    /** Fetch a cached teaser on book open. Silent on errors and on the
     *  "nothing-generated-yet" case (empty result). */
    fun loadTeaser() {
        val id = bookId ?: return
        viewModelScope.launch {
            try {
                val res = BookRepository.getTeaser(id)
                // Only surface in state if there's actually a keyframe;
                // empty response leaves teaserResult null = Idle.
                if (res.frame_exists) {
                    teaserResult = res
                }
            } catch (_: Exception) {
                // soft-fail
            }
        }
    }

    /**
     * Step 1 of the two-step UX. Renders just the keyframe + chooses
     * the scene (Sonnet + Gemini, ~30s, ~$0.04). User reviews and
     * either advances to video (generateTeaserVideo) or asks for a
     * different scene (regenerateTeaserScene).
     */
    fun generateTeaserPreview(title: String, author: String) {
        if (!requireEmailVerified()) return
        val id = bookId ?: return
        if (teaserLoading) return
        teaserError = null
        teaserLoadingKind = TeaserLoadingKind.Preview
        val key = promoKey("TEASER")
        val startedAt = kotlinx.browser.window.performance.now()
        analytics.promoGenerateStarted(AnalyticsEvents.PromoKind.TEASER_PREVIEW)
        promoCoroutineJobs[key] = viewModelScope.launch {
            val job = coroutineContext[kotlinx.coroutines.Job]
            try {
                teaserResult = BookRepository.generateTeaser(
                    id,
                    TeaserRequest(
                        title = title.trim(),
                        author = author.trim(),
                        keyframeOnly = true,
                        regeneratePrompts = true,
                        useExistingKeyframe = false,
                    ),
                )
                refreshUserCounters()
                val dur = ((kotlinx.browser.window.performance.now() - startedAt) / 1000.0).toInt()
                analytics.promoGenerateCompleted(AnalyticsEvents.PromoKind.TEASER_PREVIEW, dur)
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                teaserError = e.message ?: "Preview failed"
                analytics.promoGenerateFailed(AnalyticsEvents.PromoKind.TEASER_PREVIEW, e.message)
            } finally {
                teaserLoadingKind = null
                promoCoroutineJobs.remove(key)
                if (job?.isActive == true) persistCache()
            }
        }
        persistCache()
    }

    /**
     * Step 2a. Takes the already-generated preview keyframe and adds
     * the i2v video on top of it (Kling 3.0 Pro, ~2 min, ~$0.56). Does
     * NOT re-run Sonnet or Gemini. Requires a keyframe to already
     * exist on disk for this book.
     */
    fun generateTeaserVideo(title: String, author: String, lowQuality: Boolean = false) {
        if (!requireEmailVerified()) return
        val id = bookId ?: return
        if (teaserLoading) return
        if (teaserResult?.frame_exists != true) return
        teaserError = null
        teaserLoadingKind = TeaserLoadingKind.Video
        val key = promoKey("TEASER")
        val startedAt = kotlinx.browser.window.performance.now()
        analytics.promoGenerateStarted(
            AnalyticsEvents.PromoKind.TEASER_VIDEO,
            extra = mapOf("low_quality" to lowQuality),
        )
        promoCoroutineJobs[key] = viewModelScope.launch {
            val job = coroutineContext[kotlinx.coroutines.Job]
            try {
                teaserResult = BookRepository.generateTeaser(
                    id,
                    TeaserRequest(
                        title = title.trim(),
                        author = author.trim(),
                        keyframeOnly = false,
                        regeneratePrompts = false,
                        useExistingKeyframe = true,
                        lowQuality = lowQuality,
                    ),
                )
                refreshUserCounters()
                val dur = ((kotlinx.browser.window.performance.now() - startedAt) / 1000.0).toInt()
                analytics.promoGenerateCompleted(AnalyticsEvents.PromoKind.TEASER_VIDEO, dur)
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                teaserError = e.message ?: "Video generation failed"
                analytics.promoGenerateFailed(AnalyticsEvents.PromoKind.TEASER_VIDEO, e.message)
            } finally {
                teaserLoadingKind = null
                promoCoroutineJobs.remove(key)
                if (job?.isActive == true) persistCache()
            }
        }
        persistCache()
    }

    /**
     * Step 2b. Generates a fresh keyframe + scene, replacing the
     * current preview (or finished video). Re-runs Sonnet so the LLM
     * may pick a different scene/element this time.
     */
    fun regenerateTeaserScene(title: String, author: String) {
        // Identical request shape to generateTeaserPreview — the
        // distinction only matters for what state the UI shows during
        // the load. We mark this as "regenerate" so the spinner copy
        // can say "Picking a new scene..." instead of "Generating
        // keyframe..." (gives the user more confidence the new run is
        // actually picking different content).
        val id = bookId ?: return
        if (teaserLoading) return
        teaserError = null
        teaserLoadingKind = TeaserLoadingKind.Regenerate
        val key = promoKey("TEASER")
        val startedAt = kotlinx.browser.window.performance.now()
        analytics.promoGenerateStarted(AnalyticsEvents.PromoKind.TEASER_SCENE_REGEN)
        promoCoroutineJobs[key] = viewModelScope.launch {
            val job = coroutineContext[kotlinx.coroutines.Job]
            try {
                teaserResult = BookRepository.generateTeaser(
                    id,
                    TeaserRequest(
                        title = title.trim(),
                        author = author.trim(),
                        keyframeOnly = true,
                        regeneratePrompts = true,
                        useExistingKeyframe = false,
                    ),
                )
                refreshUserCounters()
                val dur = ((kotlinx.browser.window.performance.now() - startedAt) / 1000.0).toInt()
                analytics.promoGenerateCompleted(AnalyticsEvents.PromoKind.TEASER_SCENE_REGEN, dur)
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                teaserError = e.message ?: "Scene regeneration failed"
                analytics.promoGenerateFailed(AnalyticsEvents.PromoKind.TEASER_SCENE_REGEN, e.message)
            } finally {
                teaserLoadingKind = null
                promoCoroutineJobs.remove(key)
                if (job?.isActive == true) persistCache()
            }
        }
        persistCache()
    }

    /**
     * Download the rendered teaser MP4 to disk. Fetches bytes via the
     * Bearer-auth endpoint (no signed URL in the address bar) and
     * triggers a Blob-backed download with a title-derived filename.
     */
    fun downloadTeaserVideo(title: String) {
        val id = bookId ?: return
        val tr = teaserResult ?: return
        if (!tr.video_exists) return
        analytics.promoDownload(AnalyticsEvents.PromoKind.TEASER_VIDEO, format = "mp4")
        viewModelScope.launch {
            val bytes = BookRepository.fetchTeaserVideoBytes(id, tr.hashCode().toString()) ?: run {
                teaserError = "Video download failed"
                return@launch
            }
            val safe = title.trim().ifEmpty { "teaser" }
                .replace(Regex("""[\\/:*?"<>|]+"""), "")
                .take(80)
            jsTriggerBinaryDownload(bytes, "$safe.mp4", "video/mp4")
        }
    }

    fun selectFromHistory(item: BookHistoryItem) {
        analytics.bookSelectedFromHistory(item.aiStatus)
        // Cancel any in-flight switch so its post-suspend writes can't
        // land on the new book's screen. See `bookSwitchJob` doc.
        bookSwitchJob?.cancel()
        bookSwitchJob = viewModelScope.launch {
            val id = item.fingerprint
            pickedFileName = item.filename
            bookId = id
            AppDataStore.setCurrentBookId(id)
            analysisState = AnalysisState.Running
            errorMessage = null
            clearPerBookData()
            // Pre-set AI state from server-reported status so the UI doesn't
            // flash idle while the static analysis loads.
            aiState = when (item.aiStatus) {
                "running" -> AnalysisState.Running
                "done" -> AnalysisState.Running // probing; flips to Done below
                "failed" -> AnalysisState.Error
                else -> AnalysisState.Idle
            }
            aiStageName = if (item.aiStatus == "running") "Resuming…" else ""

            try {
                val cached = BookRepository.getCachedAnalysis(id)
                // Defence in depth: if a faster switch landed while we
                // were awaiting the cache fetch, bail out before
                // touching any book-scoped state. Cancellation usually
                // makes this unreachable, but the guard covers the
                // window between suspension points where a cooperative
                // cancel hasn't yet thrown.
                if (bookId != id) return@launch
                if (cached == null) {
                    // Legacy / pre-versioning book — its files were pruned
                    // from disk and the row can't be recovered. Backend's
                    // /history self-heals such rows, so a refetch drops
                    // the dead entry from the picker. Silently reset to
                    // idle instead of showing an error with a dead retry.
                    pickedFileName = null
                    bookId = null
                    analysisState = AnalysisState.Idle
                    AppDataStore.clearCurrentBookId()
                    refreshHistory()
                    return@launch
                }
                analysisResult = cached
                analysisState = AnalysisState.Done
                // `getCachedAnalysis` updates `lastAnalysisJson` —
                // mirror it so the next persistCache snapshots this
                // book and a refresh repaints the results instantly.
                currentAnalysisJson = BookRepository.lastAnalysisJson
                persistCache()

                // Re-attach loaders to any promo generations that are
                // still running for this book on the backend. Fires
                // BEFORE the AI-pipeline branch below because promo
                // generations can run independently of AI analysis
                // (annotation needs only the pipeline result on disk;
                // covers + teaser the same). If we waited until after
                // the AI resume, a re-attach for a 30s annotation
                // would be invisible during the multi-minute AI run.
                reattachPromoJobs(id)

                when (item.aiStatus) {
                    "running" -> {
                        val jobId = item.aiJobId
                        if (jobId != null && bookId == id) {
                            // Tracked launch so a fast subsequent switch can
                            // cancel via clearPerBookData. The resume itself
                            // also id-guards its writes as defence-in-depth.
                            aiPipelineJob?.cancel()
                            aiPipelineJob = viewModelScope.launch {
                                resumeRunningPipeline(jobId, id)
                            }
                        } else if (bookId == id) {
                            // Backend should always set aiJobId for running rows,
                            // but if it's missing, fall back to idle so the user
                            // can manually re-run.
                            aiState = AnalysisState.Idle
                        }
                    }
                    "failed" -> {
                        // Stay in Error; pre-set above. User can click "Run AI" to retry.
                    }
                    else -> {
                        // "done" or "idle" — try to load a cached report. The flag
                        // can be stale if the pipeline finished after the history
                        // snapshot, so always probe by fingerprint (the result
                        // endpoint accepts fingerprints as a legacy fallback).
                        if (loadPaidAiReport(id) && bookId == id) {
                            loadAnnotation()
                            loadCovers()
                            loadTeaser()
                        }
                    }
                }
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Re-throw so the coroutine is genuinely cancelled.
                // Without this `catch (Exception)` would swallow it,
                // letting the next-line state write happen on the new
                // book's screen after a switch — visible as a stray
                // error toast labelled with the OLD book's failure.
                throw e
            } catch (e: Exception) {
                if (bookId == id) {
                    errorMessage = e.message ?: "Failed to load"
                    analysisState = AnalysisState.Error
                }
            }
        }
    }

    /**
     * Load an AI report the user has already paid for, addressed by
     * fingerprint. Returns true when the report landed on screen.
     *
     * This GET is free — it only reads a stored report — which is what makes
     * retrying it the right move. `runAiPipeline` is not an equivalent
     * fallback: it always starts a fresh run with `force=true` and charges
     * again. That asymmetry is the whole point of this helper. Both callers
     * used to funnel every failure into [AnalysisState.Idle], which the UI
     * renders identically to "never ran" — so a 500 on this endpoint quietly
     * invited the user to pay a second time for a report they already owned.
     *
     * Outcomes, each id-guarded so a book switch mid-flight can't paint the
     * wrong screen:
     *  - report → `Done`;
     *  - 404 / null → `Idle`, the one honest "no report here";
     *  - anything else, after [AI_REPORT_FETCH_ATTEMPTS] free tries → `Error`
     *    plus a message telling the user to reload rather than re-run.
     */
    private suspend fun loadPaidAiReport(id: String): Boolean {
        repeat(AI_REPORT_FETCH_ATTEMPTS) { attempt ->
            try {
                val result = BookRepository.getPipelineResult(id)
                if (bookId != id) return false
                if (result == null) {
                    aiState = AnalysisState.Idle
                    return false
                }
                aiReport = result
                aiState = AnalysisState.Done
                return true
            } catch (e: kotlinx.coroutines.CancellationException) {
                // Never swallow cancellation — a book switch cancels this
                // coroutine, and retrying past it would write the old book's
                // report onto the new book's screen.
                throw e
            } catch (_: Exception) {
                if (bookId != id) return false
                if (attempt < AI_REPORT_FETCH_ATTEMPTS - 1) delay(1500L * (attempt + 1))
            }
        }
        if (bookId == id) {
            aiState = AnalysisState.Error
            errorMessage = (if (appLocaleSnapshot() == "ru") RuStrings else EnStrings)
                .aiReportLoadFailed
        }
        return false
    }

    private suspend fun resumeRunningPipeline(jobId: String, id: String) {
        try {
            val result = BookRepository.resumePipeline(jobId) { status ->
                // Re-attach can outlive a book switch — guard every UI write
                // so the polling loop's stage/chunk numbers never overwrite
                // the screen of the book that's currently open.
                if (bookId != id) return@resumePipeline
                aiStage = status.stage
                aiTotalStages = status.total_stages
                aiStageName = status.stage_name
                aiChunk = status.chunk
                aiTotalChunks = status.total_chunks
                aiEtaSeconds = status.eta_seconds
                aiElapsedSeconds = status.elapsed_seconds
            }
            if (bookId == id) {
                aiReport = result.report
                aiState = AnalysisState.Done
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            // Re-throw so the coroutine is genuinely cancelled.
            // Without this `catch (Exception)` would swallow it,
            // letting the next-line state write happen on the new
            // book's screen after a switch — visible as a stray
            // error toast labelled with the OLD book's failure.
            throw e
        } catch (e: Exception) {
            if (bookId == id) {
                errorMessage = "AI analysis failed: ${e.message}"
                aiState = AnalysisState.Error
            }
            // Resumed run that fails server-side gets refunded by the
            // backend; re-fetch /me to surface the restored balance.
            refreshUserCounters()
        }
        refreshHistory()
    }

    fun signIn() {
        analytics.signInClicked()
        Auth.saveState(bookId, pickedFileName, BookRepository.lastAnalysisJson)
        Auth.login()
    }

    fun signOut() {
        // Drop Firebase user bind BEFORE the redirect navigates away so
        // events queued in the next few ms aren't tagged with the
        // outgoing user. setUserId(null) reverts to anonymous install-
        // id semantics matching the EULA copy.
        analytics.logout()
        analytics.unbindUser()
        // Wipe the per-user cache BEFORE Keycloak's logout redirect
        // takes the page away. Otherwise a re-login on the same browser
        // would briefly see the old user's credits / history hydrated
        // from the leftover snapshot.
        UserCache.clear()
        Auth.logout()
    }

    fun topUpCredits() {
        viewModelScope.launch {
            credits = BookRepository.topUpCredits(10)
        }
        showTopUpDialog = false
    }

    fun redeemCode() {
        val code = redeemCodeInput.trim()
        if (code.isEmpty() || redeemInFlight) return
        redeemInFlight = true
        redeemError = null
        redeemSuccessAdded = null
        viewModelScope.launch {
            when (val result = BookRepository.redeemCode(code)) {
                is RedeemResult.Success -> {
                    credits = result.credits
                    redeemSuccessAdded = result.added
                    redeemCodeInput = ""
                    analytics.redeemAttempted("success", creditsAdded = result.added)
                }
                is RedeemResult.Failure -> {
                    redeemError = result.error
                    analytics.redeemAttempted(result.error?.take(80) ?: "unknown")
                }
            }
            redeemInFlight = false
        }
    }

    fun closeTopUpDialog() {
        showTopUpDialog = false
        redeemCodeInput = ""
        redeemError = null
        redeemSuccessAdded = null
    }

    /**
     * Pack click in TopUpDialog. Opens the payment window right away and
     * points it at the provider's page once the backend returns it, so
     * the site stays put behind it. Only if the browser blocks the window
     * does this tab go to the payment page instead. Either way the user
     * comes back to a «waiting for payment» banner and the outcome.
     *
     * [waitingText] fills the window until the page address arrives.
     */
    fun startCheckout(packId: String, region: String, waitingText: String) {
        if (checkoutInFlight) return
        if (!isLoggedIn) return
        checkoutInFlight = true
        redirectGraceJob?.cancel()
        analytics.checkoutStarted(packId, region)
        // Opened here, inside the click: a window opened after the server
        // answers no longer counts as the user's, and browsers block it.
        val payWindow = openCheckoutWindow(url = null, waitingText = waitingText)
        val openedAt = kotlin.js.Date.now()
        // Some blockers close the window a moment after it opens.
        var killedByBlocker = false
        if (payWindow != null) viewModelScope.launch {
            delay(WINDOW_KILL_CHECK_MS)
            if (isWindowClosed(payWindow)) killedByBlocker = true
        }
        viewModelScope.launch {
            when (val result = BookRepository.startCheckout(packId, region)) {
                is CheckoutResult.Redirect -> {
                    watchPendingPayment(PendingPayment(
                        id = result.paymentId,
                        packId = packId,
                        redirectUrl = result.url,
                        createdAtMs = kotlin.js.Date.now(),
                    ))
                    val windowClosed = payWindow != null && isWindowClosed(payWindow)
                    // Closed later than a blocker would: the user shut it
                    // while the server was slow, meaning to cancel. Keep
                    // this tab; the banner still offers the invoice.
                    val closedByUser = windowClosed && !killedByBlocker &&
                        kotlin.js.Date.now() - openedAt > WINDOW_KILL_CHECK_MS
                    if (payWindow != null && !windowClosed) {
                        payWindow.location.href = result.url
                        analytics.checkoutRedirected(packId, inWindow = true)
                        closeTopUpDialog()
                        // Another click is safe now: the backend hands back
                        // this same invoice for a while instead of a new one.
                        checkoutInFlight = false
                    } else if (closedByUser) {
                        closeTopUpDialog()
                        checkoutInFlight = false
                    } else {
                        analytics.checkoutRedirected(packId, inWindow = false)
                        kotlinx.browser.window.location.assign(result.url)
                        // The packs stay locked while the page unloads — a
                        // click then would only issue another invoice. If the
                        // navigation never happens, unlock them; Back from the
                        // back/forward cache cancels this (see pageshow).
                        redirectGraceJob = viewModelScope.launch {
                            delay(CHECKOUT_REDIRECT_GRACE_MS)
                            checkoutInFlight = false
                        }
                    }
                }
                is CheckoutResult.Failure -> {
                    payWindow?.let { closeIfPlaceholder(it) }
                    checkoutInFlight = false
                    paymentToast = PaymentToast.Error
                    analytics.paymentError(
                        if (result.error == "network") AnalyticsEvents.PaymentErrorStage.CHECKOUT_CALL
                        else AnalyticsEvents.PaymentErrorStage.CHECKOUT_RESPONSE,
                        result.error,
                    )
                }
            }
        }
    }

    /** Banner's «back to payment»: the same invoice, still open. */
    fun resumePendingCheckout() {
        val url = pendingPayment?.redirectUrl?.takeIf { it.isNotBlank() } ?: return
        val w = openCheckoutWindow(url)
        if (w == null) {
            kotlinx.browser.window.location.assign(url)
            return
        }
        // Some blockers close the window a moment after it opens.
        viewModelScope.launch {
            delay(WINDOW_KILL_CHECK_MS)
            if (isWindowClosed(w)) kotlinx.browser.window.location.assign(url)
        }
    }

    /**
     * The payment window: a centred popup on desktop, a new tab on phones.
     * Reuses the open one if there is one. Without a [url] it shows
     * [waitingText] until the page address is known. Null when blocked.
     *
     * Not `noopener`: the window, back on our origin, reports to its
     * opener (see [handBackToOpener]).
     */
    private fun openCheckoutWindow(url: String?, waitingText: String = ""): org.w3c.dom.Window? {
        val w = 480
        val h = 720
        val screen = kotlinx.browser.window.screen
        val left = ((screen.width - w) / 2).coerceAtLeast(0)
        val top = ((screen.height - h) / 2).coerceAtLeast(0)
        val features = "width=$w,height=$h,left=$left,top=$top,resizable=yes,scrollbars=yes"
        val opened = kotlinx.browser.window.open(url ?: "", CHECKOUT_WINDOW_NAME, features) ?: return null
        // Some in-app browsers hand back this very tab; some blockers close
        // the new window at once. Neither is a window to pay in.
        if (opened === kotlinx.browser.window || isWindowClosed(opened)) return null
        if (url == null) {
            // Same-origin about:blank, so writable. Throws when an earlier
            // payment page is still open in the window — it just stays.
            runCatching {
                val doc = opened.document
                doc.title = waitingText
                doc.body?.apply {
                    textContent = waitingText
                    setAttribute(
                        "style",
                        "margin:0;height:100vh;display:flex;align-items:center;justify-content:center;" +
                            "background:${WLColors.Surface};color:${WLColors.OnSurfaceVariant};" +
                            "font:16px system-ui,-apple-system,'Segoe UI',sans-serif",
                    )
                }
            }
        }
        runCatching { opened.focus() }
        return opened
    }

    private fun isWindowClosed(w: org.w3c.dom.Window): Boolean =
        runCatching { w.closed }.getOrDefault(true)

    /** Closes [w] only while it still shows our placeholder — never the
     *  payment page of an earlier invoice that can still be paid. */
    private fun closeIfPlaceholder(w: org.w3c.dom.Window) {
        // A provider page is cross-origin: reading its location throws.
        runCatching { if (w.location.href == "about:blank") w.close() }
    }

    /**
     * In the payment window, back from the provider at `/?payment=<id>`:
     * tell the tab that opened it — that tab shows the outcome — and
     * close. False when there's no such tab (opened directly, opener gone
     * or not ours); this window then handles the return itself.
     */
    private fun handBackToOpener(): Boolean {
        val paymentId = readPaymentIdFromUrl() ?: return false
        val opener = kotlinx.browser.window.opener ?: return false
        val origin = kotlinx.browser.window.location.origin
        val delivered = runCatching {
            val o = opener.asDynamic()
            // Reading location of a foreign-origin window throws.
            check(o.closed != true && o.location.origin == origin)
            val msg = js("({})")
            msg.type = CHECKOUT_RETURN_MESSAGE
            msg.paymentId = paymentId
            o.postMessage(msg, origin)
        }.isSuccess
        if (delivered) kotlinx.browser.window.close()
        return delivered
    }

    fun hidePendingPaymentBanner() {
        pendingPaymentBannerVisible = false
        pendingPaymentQuiet = true
        // Stays closed across reloads and in other tabs too.
        val p = pendingPayment ?: return
        AppDataStore.setPendingPayment(p.copy(quiet = true))
    }

    /**
     * Pick up the checkout the user left for: from the provider's return
     * URL (`/?payment=<id>`) or from the record [startCheckout] saved, if
     * they came back with Back, by a bookmark or hours later. Clears the
     * query string either way so a refresh or a copied link doesn't
     * replay it.
     */
    private fun resumePendingPayment() {
        val fromUrl = readPaymentIdFromUrl()
        if (fromUrl != null) clearPaymentParamFromUrl()
        if (!isLoggedIn) return
        val saved = AppDataStore.getPendingPayment()
        val pending = when {
            fromUrl == null || saved?.id == fromUrl -> saved
            // Back from a checkout another tab started: no page to return
            // to, but the outcome is still worth showing.
            else -> PendingPayment(fromUrl, packId = "", redirectUrl = "", createdAtMs = kotlin.js.Date.now())
        } ?: return
        // Already shown, here or in another tab.
        if (AppDataStore.isPaymentSettled(pending.id)) {
            forgetPendingPayment(pending.id)
            return
        }
        // A record from an earlier visit gets one quiet look: a late
        // confirmation is still news, a long-expired invoice is not.
        val stale = kotlin.js.Date.now() - pending.createdAtMs > PENDING_PAYMENT_MAX_AGE_MS
        watchPendingPayment(pending, quiet = stale || pending.quiet)
    }

    /**
     * Ways the user gets back to this tab after paying, each a cue to
     * check the payment now rather than at the next poll:
     *  - the payment window, back on our origin, reports in ([handBackToOpener]);
     *  - the tab becomes visible again (paid on the phone, switched back);
     *  - Back from the payment page restores this page from the
     *    back/forward cache as it was left — dialog open, packs locked —
     *    without re-running bootstrap, so unlock here too.
     */
    private fun installPaymentReturnListeners() {
        kotlinx.browser.window.addEventListener("message", { event ->
            val e = event.asDynamic()
            if (e.origin != kotlinx.browser.window.location.origin) return@addEventListener
            // `e.data` is already dynamic — read its fields directly.
            val data = e.data ?: return@addEventListener
            if (data.type != CHECKOUT_RETURN_MESSAGE) return@addEventListener
            val id = (data.paymentId as? String)?.takeIf { it.isNotBlank() } ?: return@addEventListener
            // A late report of a payment already shown changes nothing.
            if (!isLoggedIn || AppDataStore.isPaymentSettled(id)) return@addEventListener
            if (pendingPayment?.id == id) {
                checkPendingPaymentNow()
            } else {
                watchPendingPayment(PendingPayment(id, packId = "", redirectUrl = "", createdAtMs = kotlin.js.Date.now()))
            }
        })
        kotlinx.browser.document.addEventListener("visibilitychange", {
            if (kotlinx.browser.document.asDynamic().visibilityState == "visible") checkPendingPaymentNow()
        })
        kotlinx.browser.window.addEventListener("pageshow", { event ->
            if (event.asDynamic().persisted == true) {
                redirectGraceJob?.cancel()
                checkoutInFlight = false
                closeTopUpDialog()
                checkPendingPaymentNow()
            }
        })
    }

    /**
     * Poll `/api/payments/{id}/status` until the payment settles. Paying
     * by QR takes a while — open the bank app, scan, confirm — so this
     * keeps going well past the first minute, backing off, until the
     * provider's invoice must have expired (~30 min).
     */
    private fun watchPendingPayment(p: PendingPayment, quiet: Boolean = false) {
        AppDataStore.setPendingPayment(p)
        pendingPayment = p
        pendingPaymentBannerVisible = false
        pendingPaymentQuiet = quiet
        pendingPaymentWatch?.cancel()
        pendingPaymentWatch = viewModelScope.launch {
            val startedAt = kotlin.js.Date.now()
            while (pendingPayment?.id == p.id) {
                val expired = kotlin.js.Date.now() - p.createdAtMs > PENDING_PAYMENT_MAX_AGE_MS
                // A hidden tab waits — coming back fires visibilitychange,
                // which checks at once — except for the last look.
                if (!isPageHidden() || expired) {
                    if (checkPendingPayment(p)) revealPendingPaymentBanner(p)
                }
                if (pendingPayment?.id != p.id) break
                if (expired) {
                    forgetPendingPayment(p.id)
                    break
                }
                val watchedMs = kotlin.js.Date.now() - startedAt
                delay(
                    when {
                        watchedMs < 60_000 -> 2_000L
                        watchedMs < 5 * 60_000 -> 5_000L
                        else -> 15_000L
                    }
                )
            }
        }
    }

    /** Check right away — unless a check is already on its way (the
     *  visibility and pageshow cues often arrive together). */
    private fun checkPendingPaymentNow() {
        val p = pendingPayment ?: return
        if (pendingPaymentCheck?.isActive == true) return
        pendingPaymentCheck = viewModelScope.launch {
            if (checkPendingPayment(p)) revealPendingPaymentBanner(p)
        }
    }

    /** One /status round-trip; true only when the provider still has the
     *  payment open. A failed request is not news — the next tick asks again. */
    private suspend fun checkPendingPayment(p: PendingPayment): Boolean =
        when (val r = BookRepository.getPaymentStatus(p.id)) {
            is PaymentStatusResult.Known ->
                if (r.status.status == "PENDING") {
                    true
                } else {
                    settlePendingPayment(p, r.status)
                    false
                }
            // Not this account's payment — someone else signed in here since.
            PaymentStatusResult.NotFound -> {
                forgetPendingPayment(p.id)
                false
            }
            PaymentStatusResult.Failed -> false
        }

    /** The banner shows only once the payment is known to be still open. */
    private fun revealPendingPaymentBanner(p: PendingPayment) {
        if (pendingPayment?.id == p.id && !pendingPaymentQuiet) pendingPaymentBannerVisible = true
    }

    private fun isPageHidden(): Boolean = kotlinx.browser.document.asDynamic().hidden == true

    private fun settlePendingPayment(p: PendingPayment, s: PaymentStatusResponse) {
        // The poll loop and a re-check can both see the final status;
        // whichever gets here first settles it. JS is single-threaded, so
        // this check-and-forget can't interleave.
        if (pendingPayment?.id != p.id) return
        val quiet = pendingPaymentQuiet
        forgetPendingPayment(p.id)
        // The server's `credits` is the post-grant balance.
        credits = s.credits
        // A confirmed payment opens text editing in the reader.
        if (s.status == "CONFIRMED") canEditText = true
        // Another tab, or the payment window, may have shown it already.
        if (AppDataStore.isPaymentSettled(p.id)) return
        AppDataStore.markPaymentSettled(p.id)
        when (s.status) {
            "CONFIRMED" -> {
                paymentToast = PaymentToast.Success(s.creditsAdded)
                analytics.paymentConfirmed(
                    s.packId,
                    s.creditsAdded,
                    priceRub = pricing.packPriceRub(s.packId),
                )
                // The backend's claim dedupes this against the bootstrap one.
                viewModelScope.launch { reportPendingPurchases() }
            }
            "CANCELED", "CHARGEBACK" -> {
                if (!quiet) paymentToast = PaymentToast.Canceled
                analytics.paymentCanceled(s.packId, s.status)
            }
        }
    }

    private fun forgetPendingPayment(id: String) {
        if (AppDataStore.getPendingPayment()?.id == id) AppDataStore.clearPendingPayment()
        if (pendingPayment?.id == id) {
            pendingPayment = null
            pendingPaymentBannerVisible = false
        }
    }

    private companion object {
        const val CHECKOUT_WINDOW_NAME = "wl-checkout"
        const val CHECKOUT_RETURN_MESSAGE = "wl-checkout-return"

        /** Long enough for a blocker to close a just-opened window, too
         *  short for a person to. */
        const val WINDOW_KILL_CHECK_MS = 500L

        /** How long [startCheckout] keeps the packs locked after sending
         *  the tab to the payment page, in case the navigation never happens. */
        const val CHECKOUT_REDIRECT_GRACE_MS = 15_000L

        /** Past the provider's ~30-minute invoice lifetime plus the
         *  recovery job's catch-up, so the outcome is known by then. */
        const val PENDING_PAYMENT_MAX_AGE_MS = 45 * 60_000.0

        /** Tries [loadPaidAiReport] gives a failing report fetch before parking
         *  the book in Error. Cheap to raise — the endpoint is free — but three
         *  attempts over ~4.5s already cover a redeploy or a blip in shared
         *  storage, and beyond that the user is better served by a message. */
        const val AI_REPORT_FETCH_ATTEMPTS = 3
    }

    /**
     * Push GTM's `purchase` for every confirmed payment the backend hands
     * out. It marks each one sent as it hands it out, so repeat calls (two
     * tabs, a revisited return URL) get nothing, and a confirmation that
     * landed after polling gave up goes out on the next page load.
     */
    private suspend fun reportPendingPurchases() {
        repeat(BookRepository.claimPurchaseEvents().size) { analytics.purchaseConversion() }
    }

    private fun readPaymentIdFromUrl(): String? {
        val raw = kotlinx.browser.window.location.search.removePrefix("?")
        if (raw.isEmpty()) return null
        for (part in raw.split("&")) {
            val eq = part.indexOf('=')
            if (eq < 0) continue
            val key = part.substring(0, eq)
            if (key == "payment") {
                val value = part.substring(eq + 1)
                return kotlin.runCatching {
                    js("decodeURIComponent")(value) as String
                }.getOrNull()?.takeIf { it.isNotBlank() }
            }
        }
        return null
    }

    private fun clearPaymentParamFromUrl() {
        val loc = kotlinx.browser.window.location
        val newUrl = loc.pathname + loc.hash
        kotlinx.browser.window.history.replaceState(null, "", newUrl)
    }

    fun downloadTxt() {
        val id = bookId ?: return
        val name = (pickedFileName ?: "book").substringBeforeLast('.') + ".txt"
        viewModelScope.launch {
            BookRepository.downloadBookTxt(id, name)
        }
    }
}

/** What the teaser pipeline is currently doing — drives the loading-row
 *  copy in the UI ("Generating keyframe…" vs "Generating video…" vs
 *  "Picking a new scene…"). */
enum class TeaserLoadingKind { Preview, Video, Regenerate }
